import styled from "styled-components";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useContext, useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { theme } from "../../../../Themes/Themes";
import { SimulationsContext } from "./SimulationsTab";
import useStore from "../../../../Store/Store";
import { logger } from "../../../../utils/Logger";

const CircleDiv = styled.div`
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #fdfdfd;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  overflow: hidden;
  box-shadow: #ffaea6 0px 0px 0px 1px;
  &:hover {
    background-color: ${theme.custom.lighterPink};
    box-shadow: rgb(0 0 0 / 15%) 0px 2px 3px 1px;
  }
  &: hover path {
    fill: white;
  }
`;

// Animated play button container with gradient background cycling
const AnimatedPlayButtonContainer = styled(CircleDiv)<{
  shouldAnimate: boolean;
}>`
  ${(props) =>
    props.shouldAnimate &&
    `
    animation: playButtonBackgroundPulse 2s ease-in-out infinite;
    
    @keyframes playButtonBackgroundPulse {
      0% { background-color: #ffaea6; }
      25% { background-color: #ff9088; }
      50% { background-color: #ff7a6b; }
      75% { background-color: #ff9088; }
      100% { background-color: #ffaea6; }
    }
    
    /* Keep triangle white during animation */
    & path {
      fill: white !important;
    }
  `}

  /* When not animating, ensure triangle keeps proper color */
  ${(props) =>
    !props.shouldAnimate &&
    `
    & path {
      fill: ${theme.custom.lighterPink} !important;
    }
  `}
`;

// Regular play button arrow that stays consistent
const PlayButtonArrow = styled(ArrowRightRoundedIcon)`
  width: 140%;
  height: 140%;
  fill: ${theme.custom.lighterPink};
`;

export const SimulationControlButtons: React.FC = (): JSX.Element => {
  // Try to get context, but fallback to store if context is not available
  const context = useContext(SimulationsContext);

  const {
    simulations,
    isEditingSimulations,
    selectedSimulationKey,
    transitionToNextSimulation,
    simulationsState,
    setSimulationsState,
    currentUnsavedSimulationObject: storeCurrentUnsavedSimulationObject,
    isRunningSimulation,
    setIsRunningSimulation,
  } = useStore((state) => ({
    simulations: state.simulationsState.simulations,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    transitionToNextSimulation: state.transitionToNextSimulation,
    simulationsState: state.simulationsState,
    setSimulationsState: state.setSimulationsState,
    currentUnsavedSimulationObject: state.currentUnsavedSimulationObject,
    isRunningSimulation: state.isRunningSimulation,
    setIsRunningSimulation: state.setIsRunningSimulation,
  }));

  // Use context values if available, otherwise use store values
  const isRunning = context?.isRunning ?? isRunningSimulation;
  const currentStep = context?.currentStep ?? simulationsState.currentStep;
  const currentUnsavedSimulationObject =
    context?.currentUnsavedSimulationObject ??
    storeCurrentUnsavedSimulationObject;

  // Create setters that work with both context and store
  const setIsRunning =
    context?.setIsRunning ??
    ((running: boolean) => {
      setIsRunningSimulation(running);
    });

  const setCurrentStep =
    context?.setCurrentStep ??
    ((step: string) => {
      setSimulationsState({
        ...simulationsState,
        currentStep: step,
      });
    });

  const [disableBackward, setDisableBackward] = useState(true);
  const [disableForward, setDisableForward] = useState(false);
  const [isLastStep, setIsLastStep] = useState(false);

  const transitionToNextSimulationAndScroll = () => {
    // Don't proceed to next simulation if in edit mode
    if (isEditingSimulations) {
      return;
    }

    // At the end of current simulation, try to go to next simulation
    const simulationKeys = Object.keys(simulations);
    const currentSimIndex = simulationKeys.indexOf(selectedSimulationKey);

    // If we're at the last simulation, loop back to the first one
    const nextSimulationKey =
      currentSimIndex < simulationKeys.length - 1
        ? simulationKeys[currentSimIndex + 1]
        : simulationKeys[0];

    // Use the store action to handle all state updates atomically
    transitionToNextSimulation(nextSimulationKey);

    // Scroll to first step in the new simulation
    const simStepsList = document.getElementsByClassName("SimStepsList")[0];
    if (simStepsList) {
      simStepsList.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleForward = () => {
    let flags = useStore.getState().flags;
    if (flags.isChangingPath || flags.isHandlingSelectionChange) {
      return;
    }

    // If there's no current step, treat it the same as being at the last step
    if (!currentStep) {
      transitionToNextSimulationAndScroll();
      return;
    }

    // get the index of the current step from the simSteps array
    const currentIdx = currentUnsavedSimulationObject.simSteps.findIndex(
      (step) => step.simStepId === currentStep
    );

    if (currentIdx === currentUnsavedSimulationObject.simSteps.length - 1) {
      transitionToNextSimulationAndScroll();
      return;
    }

    let newIdx = currentIdx + 1;
    let targetStep = currentUnsavedSimulationObject.simSteps[newIdx];

    // if statement below is used to skip arrows without labels
    // if simStep at the new index satisfies the condition below, and the newIndex+1 is not out of bounds, then increment the newIdx further by 1
    if (
      (!targetStep.simStepLabel ||
        (targetStep.simStepLabel === targetStep.simStepId &&
          targetStep.isEdge)) &&
      !isEditingSimulations &&
      newIdx + 1 < currentUnsavedSimulationObject.simSteps.length
    ) {
      newIdx++;
    }

    const nextStepId =
      currentUnsavedSimulationObject.simSteps[newIdx].simStepId;
    setCurrentStep(nextStepId);

    const simStepsList = document.getElementsByClassName("SimStepsList")[0];
    if (simStepsList) {
      simStepsList.scrollTo({
        top: 36 * newIdx - simStepsList.clientHeight / 2,
        behavior: "smooth",
      });
    }

    // Trigger enhanced code scrolling to ensure highlighted code is visible
    setTimeout(() => {
      useStore.getState().triggerEnhancedCodeScroll();
    }, 150); // Small delay to let the step change complete
  };

  const handleBackward = () => {
    let flags = useStore.getState().flags;
    if (flags.isChangingPath || flags.isHandlingSelectionChange) return;
    if (currentStep) {
      const currentIdx = currentUnsavedSimulationObject.simSteps.findIndex(
        (step) => step.simStepId === currentStep
      );
      if (currentIdx === 0) {
        return;
      }
      let newIdx = currentIdx - 1;
      // if statement below is used to skip arrows without labels
      // if simStep at the new index satisfies the condition below, and the newIndex-1 is not out of bounds, then decrement the newIdx further by 1
      let targetStep = currentUnsavedSimulationObject.simSteps[newIdx];
      if (
        (!targetStep.simStepLabel ||
          (targetStep.simStepLabel === targetStep.simStepId &&
            targetStep.isEdge)) &&
        !isEditingSimulations &&
        newIdx - 1 > 0
      ) {
        newIdx--;
      }
      setCurrentStep(currentUnsavedSimulationObject.simSteps[newIdx].simStepId);

      const simStepsList = document.getElementsByClassName("SimStepsList")[0];
      // them scroll the one that's found into view
      simStepsList.scrollTo({
        top: 36 * newIdx - simStepsList.clientHeight / 2,
        behavior: "smooth",
      });

      // Trigger enhanced code scrolling to ensure highlighted code is visible
      setTimeout(() => {
        useStore.getState().triggerEnhancedCodeScroll();
      }, 150); // Small delay to let the step change complete
    }
  };

  const handleStop = () => {
    setIsRunning(false);
    setDisableForward(true);
    setDisableBackward(true);
    setCurrentStep("");
  };

  const handleStart = () => {
    setIsRunning(true);
    setDisableForward(false);
    setDisableBackward(false);
  };

  // Disables forward or backward buttons when the current step is the first or last step
  useEffect(() => {
    if (!selectedSimulationKey || !isRunning) {
      setDisableBackward(true);
      setDisableForward(true);
      return;
    }
    if (currentStep) {
      const currentIdx = currentUnsavedSimulationObject.simSteps.findIndex(
        (step) => step.simStepId === currentStep
      );

      if (currentIdx === 0) {
        setDisableBackward(true);
      } else {
        // disable backward button for 300ms to prevent infinite loop
        setDisableBackward(true);
        setTimeout(() => {
          setDisableBackward(false);
        }, 300);
      }

      const isLastStepOfSimulation =
        currentIdx === currentUnsavedSimulationObject.simSteps.length - 1;
      if (isLastStepOfSimulation) {
        // Only disable forward if in edit mode - we can always loop around
        const shouldDisable = Boolean(isEditingSimulations);
        setDisableForward(shouldDisable);
      } else {
        // disable forward button for 300ms to prevent infinite loop
        setDisableForward(true);
        setTimeout(() => {
          setDisableForward(false);
        }, 300);
      }
    }
  }, [
    currentStep,
    currentUnsavedSimulationObject.simSteps,
    selectedSimulationKey,
    isRunning,
    simulations,
    isEditingSimulations,
  ]);

  // Effect to track whether we're on the last step of the current simulation
  useEffect((): void => {
    // Reset isLastStep if no simulation is selected or simulation isn't running
    if (!selectedSimulationKey || !isRunning) {
      setIsLastStep(false);
      return;
    }

    // If there's no current step, we're effectively at the last step
    if (!currentStep) {
      setIsLastStep(true);
      return;
    }

    // Find the index of the current step in the simulation steps array
    const currentIdx = currentUnsavedSimulationObject.simSteps.findIndex(
      (step) => step.simStepId === currentStep
    );

    // Check if current index is the last index in the steps array
    const isLast =
      currentIdx === currentUnsavedSimulationObject.simSteps.length - 1;
    setIsLastStep(isLast);
  }, [
    currentStep,
    currentUnsavedSimulationObject.simSteps,
    selectedSimulationKey,
    isRunning,
  ]);

  return (
    <div className="simulationsPlayerBar">
      <CircleDiv
        className={`simulationControlButton backward ${
          disableBackward && "disabledSimButtom"
        }`}
        style={{ cursor: disableBackward ? "not-allowed" : "pointer" }}
      >
        <NavigateBeforeRoundedIcon
          style={{
            width: "100%",
            height: "100%",
            fill: theme.custom.lighterPink,
          }}
          fontSize="large"
          onClick={handleBackward}
        />
      </CircleDiv>

      <AnimatedPlayButtonContainer
        className={`simulationControlButton stop ${
          (isEditingSimulations || !selectedSimulationKey) &&
          "disabledSimButtom"
        }`}
        style={{
          display: isEditingSimulations ? "none" : "flex",
        }}
        shouldAnimate={selectedSimulationKey !== "" && !isRunning}
      >
        {isRunning ? (
          <StopRoundedIcon
            data-testid="stopButton"
            style={{
              width: "70%",
              height: "70%",
              fill: theme.custom.lighterPink,
            }}
            onClick={handleStop}
          />
        ) : (
          <PlayButtonArrow
            style={{
              width: "140%",
              height: "140%",
            }}
            fontSize="large"
            onClick={handleStart}
          />
        )}
      </AnimatedPlayButtonContainer>
      <CircleDiv
        className={`simulationControlButton forward ${
          disableForward && "disabledSimButtom"
        }`}
        style={{ cursor: disableForward ? "not-allowed" : "pointer" }}
      >
        <Tooltip title={isLastStep ? "Go to next simulation" : "Next step"}>
          <span style={{ display: "flex", width: "100%", height: "100%" }}>
            {isLastStep ? (
              <KeyboardDoubleArrowRightIcon
                style={{
                  width: "100%",
                  height: "100%",
                  fill: theme.custom.lighterPink,
                }}
                onClick={handleForward}
              />
            ) : (
              <NavigateNextRoundedIcon
                style={{
                  width: "100%",
                  height: "100%",
                  fill: theme.custom.lighterPink,
                }}
                onClick={handleForward}
              />
            )}
          </span>
        </Tooltip>
      </CircleDiv>
    </div>
  );
};

export default SimulationControlButtons;
